import * as Sentry from '@sentry/react'
import LogRocket from 'logrocket'
import LogRocketFuzzySanitizer from 'logrocket-fuzzy-search-sanitizer'
import { PropsWithChildren, useEffect } from 'react'

import { useAdminContext } from '~/components/hooks/useAdminContext'
import { useCurrentUser } from '~/components/hooks/useCurrentUser'
import { useFirm } from '~/components/hooks/useFirm'
import { isProd } from '~/util/env'
import { FIRM_SIDS_TO_EXCLUDE_FROM_SESSION_LOGGING } from '~/util/logging'

export function LogRocketWrapper({ children }: PropsWithChildren) {
  const user = useCurrentUser()
  const firm = useFirm()
  const { isImpersonating, impersonatedUser, adminUser } = useAdminContext()
  const effectiveUser = isImpersonating ? impersonatedUser : user
  const effectiveUserFullName = isImpersonating
    ? `${impersonatedUser?.fullName} (${adminUser?.fullName})`
    : user.fullName
  const effectiveUserEmail = effectiveUser.email
  const effectiveUserRole = effectiveUser.role
  const effectiveUserSid = effectiveUser.sid
  useEffect(
    function initLogRocket() {
      if (isProd && firm && !FIRM_SIDS_TO_EXCLUDE_FROM_SESSION_LOGGING.includes(firm.sid)) {
        const SENSITIVE_HEADERS = [
          'Authorization',
          'Cookie',
          'Set-Cookie',
          'X-CSRF-Token',
          'X-XSRF-Token',
          'Session',
          'X-API-Key',
          'Bearer',
          'access_token',
          'refresh_token',
          'id_token',
          'token',
          'sessionid',
          'x-auth-token',
        ]

        const { requestSanitizer, responseSanitizer } =
          LogRocketFuzzySanitizer.setup(SENSITIVE_HEADERS)

        LogRocket.init('lhdfyv/prod', {
          network: {
            requestSanitizer: request => {
              const requestWithBody = { ...request, body: request.body ?? null }
              return requestSanitizer(requestWithBody)
            },
            responseSanitizer: response => {
              const responseWithBody = { ...response, body: response.body ?? null }
              return responseSanitizer(responseWithBody)
            },
          },
        })
        LogRocket.identify(effectiveUserSid, {
          name: effectiveUserFullName,
          email: effectiveUserEmail,
          role: effectiveUserRole,
          firmSid: firm.sid,
          firmName: firm.name,
          isImpersonating,
        })
        LogRocket.getSessionURL(sessionURL => {
          Sentry.setExtra('sessionURL', sessionURL)
        })
      }
    },
    [
      firm,
      effectiveUserEmail,
      effectiveUserFullName,
      effectiveUserRole,
      effectiveUserSid,
      isImpersonating,
    ]
  )
  return <>{children}</>
}

export function logRocketTrackEvent(
  { name, firmSid }: { name: string; firmSid: string },
  data: unknown
) {
  try {
    if (isProd && firmSid && !FIRM_SIDS_TO_EXCLUDE_FROM_SESSION_LOGGING.includes(firmSid)) {
      LogRocket.track(name, { data: JSON.stringify(data) })
    }
  } catch (error) {
    console.error(error)
  }
}
